/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable react/jsx-curly-newline */
/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import moment from 'moment';
import { Button, Spinner } from '@gsa/afp-component-library';
import { useAppAbility, useCurrentUser } from '@gsa/afp-shared-ui-utils';
import DetailTable from '../../../../components/DetailTable';
import { OPERATIONS, SUBJECTS } from '../../../../utilities/constants';
import MODAL_MODES from '../constants';
import RevealText from '../../components/reveal-text';
import { useFederalStandards } from '../../fvs-provider';
import { useFvsLandingPageContext } from '../fvs-landing-page-provider';
import { mapFvsStatusToTags, mapReviewStatusToTag } from '../helper';
import { getRole } from '../../../../utilities/types/role-types';

const FVSDetails = () => {
  const {
    vehicleStandard,
    vehicleStandardsLoading,
    isCommentingAllowed4Fvs4User,
  } = useFederalStandards();

  const {
    infoCopy,
    setFvsLandingPageData,
    getAnnouncementForFvsInfo,
  } = useFvsLandingPageContext();

  const [standardsData, setStandardsData] = useState([]);
  const [psoCommentingPeriodEnd, setPsoCommentingPeriodEnd] = useState('');
  const ability = useAppAbility();
  const [publicCommentingPeriodEnd, setPublicCommentingPeriodEnd] = useState(
    '',
  );
  const {
    currentUser: { roles },
  } = useCurrentUser();

  const role = getRole(roles);

  // AFP-229609 - 'Review status' is for engineers, senior engineers and site
  // administrators. The service withholds reviewStatus from everyone else, so
  // this only decides whether to draw the row; it is not the security boundary.
  const canViewReviewStatus = ability.can(OPERATIONS.Update, SUBJECTS.FVS);

  const handleData = () => {
    const tableData = [
      {
        header: 'Contract year',
        value: vehicleStandard?.year,
        type: 'text',
      },
      {
        header: 'Federal Standard Code',
        value: `${vehicleStandard?.fedStandardCode} - ${vehicleStandard?.fedStandard?.title}`,
        type: 'text',
      },
      {
        header: 'Status',
        statuses: mapFvsStatusToTags(vehicleStandard),
        type: 'multi_status',
      },
    ];

    const reviewStatusTag = canViewReviewStatus
      ? mapReviewStatusToTag(vehicleStandard?.reviewStatus)
      : null;

    if (reviewStatusTag) {
      tableData.push({
        header: 'Review status',
        value: reviewStatusTag.value,
        variant: reviewStatusTag.variant,
        type: 'status',
      });
    }

    setStandardsData(tableData);
  };

  useEffect(() => {
    if (vehicleStandard) {
      handleData();
      if (vehicleStandard?.commentingPeriods) {
        const currentPsoOpenPeriod = vehicleStandard.commentingPeriods.find(
          (c) => c.status === 'Open' && c.userType === 'VehicleSupplier',
        );
        if (currentPsoOpenPeriod) {
          setPsoCommentingPeriodEnd(
            moment(currentPsoOpenPeriod.periodEnd).format('l'),
          );
        }
      }
      if (vehicleStandard?.commentingPeriods) {
        const currentPublicOpenPeriod = vehicleStandard.commentingPeriods.find(
          (c) => c.status === 'Open' && c.userType === 'Public',
        );
        if (currentPublicOpenPeriod) {
          setPublicCommentingPeriodEnd(
            moment(currentPublicOpenPeriod.periodEnd).format('l'),
          );
        }
      }
    }

    handleData();
  }, [vehicleStandard]);

  useEffect(() => {
    getAnnouncementForFvsInfo({
      variables: { boardType: 2 },
    });
  }, []);

  const editButton = () => (
    <Button
      aria-label="edit announcement modal"
      onClick={() =>
        setFvsLandingPageData('SET_MODAL_MODE', MODAL_MODES.INFO_RTE)
      }
      className="margin-left-1"
      variant="unstyled"
      data-testid="edit-announcement"
      label="Edit"
      leftIcon={{
        name: 'edit',
        className: 'usa-icon--size-2',
      }}
    />
  );

  const renderEditButton = ability.can(
    OPERATIONS.Approve,
    SUBJECTS.CatalogCodes,
  )
    ? editButton()
    : null;

  return (
    <>
      {vehicleStandardsLoading ? (
        <Spinner size="small" />
      ) : (
        <>
          <h2 className="margin-top-neg-1" tabIndex="0">
            Details
          </h2>

          <div className="grid-row grid-gap">
            <div className="desktop:grid-col-9 tablet-lg:grid-col-12 padding-bottom-2 margin-right-2">
              <DetailTable className="fvs-info-table" data={standardsData} />
            </div>
            <div className="desktop:grid-col-3" />
          </div>

          {(() => {
            switch (true) {
              case role === 'Engineer':
                return null;
              case isCommentingAllowed4Fvs4User &&
                psoCommentingPeriodEnd &&
                psoCommentingPeriodEnd !== '' &&
                (role === 'PSO' || role === 'Vehicle Supplier'):
                return (
                  <div className="grid-row grid-gap">
                    <div className="desktop:grid-col-8 tablet-lg:grid-col-12 padding-top-2 margin-right-2">
                      <span className="body-bold" tabIndex="0">
                        The current commenting period for vehicle suppliers is
                        open through {psoCommentingPeriodEnd}.
                      </span>
                      <ul>
                        <li>
                          <span className="body" tabIndex="0">
                            To comment on a Federal Standard Code Documentation,
                            select a document within the Documents tab below.
                          </span>
                        </li>
                        <li>
                          <span className="body" tabIndex="0">
                            To comment on a Standard Item or its related
                            Equipment Code, navigate to a Standard Item using
                            the menu below.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              case isCommentingAllowed4Fvs4User &&
                publicCommentingPeriodEnd &&
                publicCommentingPeriodEnd !== '' &&
                (role !== 'PSO' || role !== 'Vehicle Supplier'):
                return (
                  <div className="grid-row grid-gap">
                    <div className="desktop:grid-col-8 tablet-lg:grid-col-12 padding-top-2 margin-right-2">
                      <span className="body-bold" tabIndex="0">
                        The current commenting period for public is open through{' '}
                        {publicCommentingPeriodEnd}.
                      </span>
                      <ul>
                        <li>
                          <span className="body" tabIndex="0">
                            To comment on a Federal Standard Code Documentation,
                            select a document within the Documents tab below.
                          </span>
                        </li>
                        <li>
                          <span className="body" tabIndex="0">
                            To comment on a Standard Item or its related
                            Equipment Code, navigate to a Standard Item using
                            the menu below.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              default:
                return null;
            }
          })()}
          <RevealText
            // expanded if PSO/Public
            isExpanded={!ability.can(OPERATIONS.Update, SUBJECTS.FVS)}
            className="margin-top-3"
            title="About Standard Items"
            copy={
              <>
                {ReactHtmlParser(infoCopy)}
                {renderEditButton}
              </>
            }
          />
        </>
      )}
    </>
  );
};

export default FVSDetails;
